import React, { useState } from 'react'
import styled from 'styled-components'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import OwnerPicker from 'features/actions/workspace/OwnerPicker'
import { PRIORITY_LEVELS, PRIORITY_META, priorityKey } from 'features/taskManagement/taskPriority'
import { PRIVATE_PATHS } from 'utils/paths'
import FileList from './FileList'

const DEFAULT_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'na', label: 'N/A' },
]

export const SCORED_QUESTION_OPTIONS = [
  { value: '2', label: '2 - Good standard', score: 2 },
  { value: '1', label: '1 - Needs improvement', score: 1 },
  { value: '0', label: '0 - Fail', score: 0 },
  { value: 'na', label: 'N/A' },
]

export const scoreTint = (score, max) => {
  if (score === undefined || score === null || !max) return { fg: '#475467', bg: '#f2f4f7' }
  if (score >= max) return { fg: '#15803d', bg: '#dcfce7' }
  if (score <= 0) return { fg: '#b00020', bg: '#fdecea' }
  return { fg: '#b45309', bg: '#fef3c7' }
}

const PRIORITIES_LOW_FIRST = [...PRIORITY_LEVELS].reverse()

const newRowId = () => `row_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

export const normaliseAudit = value => {
  const v = value && typeof value === 'object' ? value : {}
  return {
    response: v.response || '',
    comments: v.comments || '',
    actions: (Array.isArray(v.actions) ? v.actions : []).map((row, index) => ({
      id: row.id || `row_${index}`,
      action: row.action || '',
      priority: row.priority ? priorityKey(row.priority) : '',
      assigneeId: row.assigneeId || row.assignee_id || '',
      assigneeName: row.assigneeName || row.assignee_name || '',
      dueDate: row.dueDate || row.due_date || '',
      remedialId: row.remedialId || row.remedial_id || '',
    })),
    photos: Array.isArray(v.photos) ? v.photos : [],
  }
}

const toStored = audit => ({
  response: audit.response,
  comments: audit.comments,
  actions: audit.actions.map(row => ({
    id: row.id,
    action: row.action,
    priority: row.priority,
    assignee_id: row.assigneeId || null,
    assignee_name: row.assigneeName,
    due_date: row.dueDate,
    ...(row.remedialId ? { remedial_id: row.remedialId } : {}),
  })),
  photos: audit.photos,
})

const formatDate = iso => {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB')
}

const AuditQuestionField = ({ field, value, onChange, readOnly, taskId, task, fieldId, isRequired }) => {
  const audit = normaliseAudit(value)
  const scored = field.type === 'scored_question'
  const options = field.options?.length ? field.options : (scored ? SCORED_QUESTION_OPTIONS : DEFAULT_OPTIONS)
  const maxScore = scored ? Math.max(0, ...options.map(o => (typeof o.score === 'number' ? o.score : -Infinity))) : null
  const [picker, setPicker] = useState({ open: false, anchor: null, rowId: null })
  const bpId = task?.buildingPassportId || task?.building?.id || task?.buildingPassport?.id || null

  const update = patch => onChange(toStored({ ...audit, ...patch }))
  const updateRow = (id, patch) => update({ actions: audit.actions.map(row => (row.id === id ? { ...row, ...patch } : row)) })
  const addRow = () => update({ actions: [...audit.actions, { id: newRowId(), action: '', priority: 'medium', assigneeId: '', assigneeName: '', dueDate: '', remedialId: '' }] })
  const removeRow = id => update({ actions: audit.actions.filter(row => row.id !== id) })

  const questionId = `${fieldId}_question`

  return (
    <Card>
      <Question id={questionId}>
        {field.number && <QuestionNumber>{field.number}</QuestionNumber>}
        <span>
          {field.label}
          {isRequired && <Required aria-hidden='true'> *</Required>}
        </span>
      </Question>
      {field.description && <Help>{field.description}</Help>}

      <Label as='div' id={`${fieldId}_response_label`}>{scored ? 'Score' : 'Response'}</Label>
      <Choices role='radiogroup' aria-labelledby={`${questionId} ${fieldId}_response_label`}>
        {options.map(option => {
          const active = audit.response === option.value
          const tint = scored ? scoreTint(option.score, maxScore) : null
          return (
            <Choice
              key={option.value}
              type='button'
              role='radio'
              aria-checked={active}
              $active={active}
              $tint={tint?.fg}
              $bg={tint?.bg}
              disabled={readOnly}
              onClick={() => !readOnly && update({ response: active ? '' : option.value })}
            >
              {option.label}
            </Choice>
          )
        })}
      </Choices>

      <Label htmlFor={`${fieldId}_comments`}>Comments</Label>
      {readOnly ? (
        <ReadText>{audit.comments || 'No comments.'}</ReadText>
      ) : (
        <Textarea
          id={`${fieldId}_comments`}
          rows={3}
          value={audit.comments}
          onChange={e => update({ comments: e.target.value })}
          placeholder='What did you find?'
        />
      )}

      <SubHead>
        <Label as='div'>Actions ({audit.actions.length})</Label>
        {!readOnly && <SubHelp>Record anything that needs putting right. Each action becomes a remedial on this building, with the priority, person and due date you choose, when the audit is submitted.</SubHelp>}
      </SubHead>
      {audit.actions.length === 0 && readOnly && <ReadText>No actions.</ReadText>}
      {audit.actions.map((row, index) => (
        <ActionCard key={row.id}>
          <ActionTop>
            <ActionTitle>Action {index + 1}</ActionTitle>
            {row.remedialId && bpId && (
              <RemedialLink href={`${PRIVATE_PATHS.remedials.replace(':bpId', bpId)}?q=${encodeURIComponent(row.action.slice(0, 60))}`}>
                Remedial raised: view it
              </RemedialLink>
            )}
            {!readOnly && !row.remedialId && (
              <RemoveBtn type='button' onClick={() => removeRow(row.id)} aria-label={`Remove action ${index + 1}`}>
                <DeleteOutlineRoundedIcon style={{ fontSize: 18 }} /> Remove
              </RemoveBtn>
            )}
          </ActionTop>

          <Label htmlFor={`${fieldId}_${row.id}_action`}>What needs to be done</Label>
          {readOnly || row.remedialId ? (
            <ReadText>{row.action || 'Not filled in.'}</ReadText>
          ) : (
            <Textarea
              id={`${fieldId}_${row.id}_action`}
              rows={2}
              value={row.action}
              onChange={e => updateRow(row.id, { action: e.target.value })}
              placeholder='e.g. Replace the damaged smoke seals on the second floor fire doors'
            />
          )}

          <Grid>
            <PriorityCell>
              <Label as='div' id={`${fieldId}_${row.id}_priority`}>Priority</Label>
              <Choices role='radiogroup' aria-labelledby={`${fieldId}_${row.id}_priority`}>
                {PRIORITIES_LOW_FIRST.map(level => {
                  const meta = PRIORITY_META[level]
                  const active = row.priority === level
                  return (
                    <Choice
                      key={level}
                      type='button'
                      role='radio'
                      aria-checked={active}
                      $active={active}
                      $tint={meta.fg}
                      $bg={meta.bg}
                      title={meta.hint}
                      disabled={readOnly || !!row.remedialId}
                      onClick={() => updateRow(row.id, { priority: level })}
                    >
                      {meta.label}
                    </Choice>
                  )
                })}
              </Choices>
            </PriorityCell>

            <div>
              <Label as='div'>Assigned to</Label>
              {readOnly || row.remedialId ? (
                <ReadText>{row.assigneeName || 'Not assigned'}</ReadText>
              ) : (
                <PersonBtn
                  type='button'
                  onClick={e => setPicker({ open: true, anchor: e.currentTarget, rowId: row.id })}
                  aria-label={row.assigneeName ? `Assigned to ${row.assigneeName}. Change person` : 'Choose who this action is assigned to'}
                >
                  <PersonOutlineRoundedIcon style={{ fontSize: 18 }} />
                  {row.assigneeName || 'Choose a person'}
                </PersonBtn>
              )}
            </div>

            <div>
              <Label htmlFor={`${fieldId}_${row.id}_due`}>Due date</Label>
              {readOnly || row.remedialId ? (
                <ReadText>{formatDate(row.dueDate) || 'No due date'}</ReadText>
              ) : (
                <DateInput
                  id={`${fieldId}_${row.id}_due`}
                  type='date'
                  value={row.dueDate || ''}
                  onChange={e => updateRow(row.id, { dueDate: e.target.value })}
                />
              )}
            </div>
          </Grid>
        </ActionCard>
      ))}
      {!readOnly && (
        <AddBtn type='button' onClick={addRow}>
          <AddRoundedIcon style={{ fontSize: 18 }} /> Add an action
        </AddBtn>
      )}

      <SubHead>
        <Label as='div'>Photos ({audit.photos.length})</Label>
        <SubHelp>Photos are numbered as figures in the audit report.</SubHelp>
      </SubHead>
      <FileList
        files={audit.photos}
        onChange={photos => update({ photos })}
        readOnly={readOnly}
        taskId={taskId}
        photosOnly
        emptyText='No photos taken.'
      />

      <OwnerPicker
        anchorEl={picker.anchor}
        open={picker.open}
        onClose={() => setPicker({ open: false, anchor: null, rowId: null })}
        buildingPassportId={bpId}
        currentOwnerId={audit.actions.find(r => r.id === picker.rowId)?.assigneeId || null}
        title='Who is this action assigned to?'
        onSelect={person => {
          if (picker.rowId) {
            updateRow(picker.rowId, { assigneeId: person?.id || '', assigneeName: person?.name || '' })
          }
          setPicker({ open: false, anchor: null, rowId: null })
        }}
      />
    </Card>
  )
}

export default AuditQuestionField

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  background: #fff;
`

const Question = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.45;
  color: #101828;
`

const QuestionNumber = styled.span`
  flex: 0 0 auto;
  min-width: 38px;
  padding: 2px 8px;
  border-radius: 8px;
  background: #e6f7f9;
  color: #1f7f89;
  font-size: 14px;
  font-weight: 800;
  text-align: center;
`

const Required = styled.span`
  color: #b00020;
`

const Help = styled.p`
  margin: 0;
  font-size: 14px;
  color: #475467;
`

const Label = styled.label`
  display: block;
  margin-top: 6px;
  font-size: 14px;
  font-weight: 700;
  color: #344054;
`

const Choices = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const Choice = styled.button`
  font: inherit;
  min-height: 44px;
  min-width: 72px;
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  border: 1.5px solid ${({ $active, $tint }) => ($active ? ($tint || '#33becc') : '#d0d5dd')};
  background: ${({ $active, $bg }) => ($active ? ($bg || '#e6f7f9') : '#fff')};
  color: ${({ $active, $tint }) => ($active ? ($tint || '#1f7f89') : '#344054')};
  &:hover:not(:disabled) { border-color: ${({ $tint }) => $tint || '#33becc'}; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
  &:disabled { cursor: default; opacity: ${({ $active }) => ($active ? 1 : 0.55)}; }
`

const Textarea = styled.textarea`
  font: inherit;
  font-size: 15px;
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  resize: vertical;
  &:focus { outline: none; border-color: #33becc; box-shadow: 0 0 0 3px rgba(51, 190, 204, 0.15); }
`

const ReadText = styled.p`
  margin: 0;
  font-size: 15px;
  color: #344054;
  white-space: pre-line;
`

const SubHead = styled.div`
  margin-top: 6px;
  padding-top: 10px;
  border-top: 1px solid #f2f4f7;
`

const SubHelp = styled.p`
  margin: 2px 0 0;
  font-size: 13px;
  color: #667085;
`

const ActionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  background: #fcfcfd;
`

const ActionTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`

const ActionTitle = styled.span`
  font-size: 14px;
  font-weight: 800;
  color: #101828;
`

const RemedialLink = styled.a`
  font-size: 13px;
  font-weight: 700;
  color: #1f7f89;
`

const RemoveBtn = styled.button`
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 40px;
  padding: 6px 10px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  color: #344054;
  font-size: 14px;
  cursor: pointer;
  &:hover { border-color: #b00020; color: #b00020; }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  @media (max-width: 768px) { grid-template-columns: minmax(0, 1fr); }
`

const PriorityCell = styled.div`
  grid-column: 1 / -1;
`

const PersonBtn = styled.button`
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-height: 44px;
  padding: 8px 12px;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  background: #fff;
  color: #101828;
  font-size: 15px;
  text-align: left;
  cursor: pointer;
  &:hover { border-color: #33becc; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

const DateInput = styled.input`
  font: inherit;
  font-size: 15px;
  width: 100%;
  box-sizing: border-box;
  min-height: 44px;
  padding: 8px 10px;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
`

const AddBtn = styled.button`
  font: inherit;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 8px 16px;
  border: 1.5px dashed #33becc;
  border-radius: 10px;
  background: #fff;
  color: #1f7f89;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: #e6f7f9; }
`
